/**
 * Form02_AuthorizationForm.jsx
 * Collection: authorization_forms
 * Redirect: /forms/03-chronic-care-management
 */
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  FormShell, Field, CheckGroup, SigBlock,
  saveToMongo, downloadPDF, useToast, C,
} from "../shared/formKit";

const API_URL = "/api/forms/authorization-form";
const NEXT    = "/forms/03-chronic-care-management";

export default function AuthorizationForm({ patientName="", practiceName="", onSubmit }) {
  const history = useHistory();
  const formRef  = useRef(null);
  const sigRef   = useRef(null);
  const [sigEmpty, setSigEmpty]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId]       = useState(null);
  const [showToast, Toast]          = useToast();

  const [form, setForm] = useState({
    authorizations: {
      payBenefits:      false,
      releaseInfo:      false,
      patientPicture:   false,
      privacyNotice:    false,
    },
    printName:        "",
    responsibleParty: "",
    relationship:     "",
    sigDate:          new Date().toISOString().split("T")[0],
  });

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const setAuth = (k,v) => setForm(f=>({...f, authorizations:{...f.authorizations,[k]:v}}));
  const getSig  = () => (!sigRef.current||sigRef.current.isEmpty()) ? null : sigRef.current.toDataURL("image/png");

  const buildDoc = () => ({
    formType:       "authorization_form",
    patient:        { name: patientName },
    practiceName,
    authorizations: { ...form.authorizations },
    printName:      form.printName,
    authorization: {
      responsibleParty: form.responsibleParty,
      relationship:     form.relationship,
      sigDate:          form.sigDate || null,
      signatureDataURL: getSig(),
    },
    meta: { submittedAt: new Date().toISOString(), formVersion:"1.0.0" },
  });

  const handleSubmit = async () => {
    if (sigEmpty) { showToast("Please sign before submitting.","error"); return; }
    setSubmitting(true);
    try {
      const doc = buildDoc();
      const res = typeof onSubmit==="function" ? await onSubmit(doc) : await saveToMongo(doc, API_URL);
      if (res?._id) setSavedId(res._id);
      showToast("Saved! Redirecting…","success");
      setTimeout(()=>history.push(NEXT),1800);
    } catch(e){ console.error(e); showToast("Save failed.","error"); }
    finally { setSubmitting(false); }
  };

  const handleDownload = async () => {
    try { await downloadPDF(formRef,`authorization-form-${patientName||"patient"}.pdf`); showToast("Downloaded!","success"); }
    catch { showToast("PDF failed.","error"); }
  };

  const practice = practiceName || "_PRACTICE_NAME_";

  return (
    <FormShell title="Authorization Form" subtitle={`Welcome to ${practice} — Please sign and return to receptionist.`}
      dbLabel="MongoDB · authorization_forms"
      patientName={patientName} savedId={savedId} formRef={formRef}
      onSubmit={handleSubmit} onDownload={handleDownload} submitting={submitting}>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">!</span>Important Notice</div>
        <div className="wf-policy">
          <p>You are to provide copies of your current Insurance Card(s) &amp; Driver's License or Photo ID. We reserve the right to refuse treatment if these items are not provided. We also reserve the right to refuse treatment to those persons who use vulgarity or threats to staff or physicians.</p>
        </div>
      </div>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">2</span>Authorizations — Select all that apply</div>

        {[
          ["payBenefits",    "Authorization to Pay Benefits to Facility",
           `I hereby authorize payment directly to ${practice}. Not to exceed the reasonable and customary charge for those services. I understand the provider's charge may exceed private insurance carrier payment, and if greater than such payment, I will be held responsible for that amount.`],
          ["releaseInfo",    "Authorization to Release Information",
           `I hereby authorize the treating provider to release any information required in the course of my examination or treatment to my insurance company, providers, individuals and entities authorized by me or their contracted entities. (If patient is a minor, parent must sign.)`],
          ["patientPicture", "Authorization for Patient Picture",
           `I hereby authorize ${practice} to take a picture for my electronic medical record.`],
          ["privacyNotice",  "Authorization for Receipt of Privacy Practices Notice",
           `I acknowledge receipt of Notice of Privacy Practices (Pages 1, 2, and 3) upon the present visit or within the last twelve calendar months.`],
        ].map(([key, heading, body]) => (
          <div key={key} style={{marginBottom:16, padding:"14px 16px", border:`1.5px solid ${form.authorizations[key]?C.teal:C.border}`,
            borderRadius:10, background: form.authorizations[key]?C.tealLt:C.white, transition:"all .2s"}}>
            <label style={{display:"flex", gap:12, cursor:"pointer", alignItems:"flex-start"}}>
              <input type="checkbox" style={{marginTop:3, accentColor:C.teal, flexShrink:0}}
                checked={form.authorizations[key]} onChange={e=>setAuth(key,e.target.checked)} />
              <div>
                <div style={{fontWeight:600, fontSize:14, marginBottom:4}}>{heading}</div>
                <div style={{fontSize:13, color:C.muted, lineHeight:1.6}}>{body}</div>
              </div>
            </label>
          </div>
        ))}
      </div>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">3</span>Print Name</div>
        <Field label="Print Full Name">
          <input type="text" value={form.printName} onChange={e=>set("printName",e.target.value)} placeholder="Print your full name" />
        </Field>
      </div>

      <SigBlock sigRef={sigRef} sigEmpty={sigEmpty} setSigEmpty={setSigEmpty}
        responsibleParty={form.responsibleParty} setResponsibleParty={v=>set("responsibleParty",v)}
        relationship={form.relationship} setRelationship={v=>set("relationship",v)}
        sigDate={form.sigDate} setSigDate={v=>set("sigDate",v)} showRelationship={false} />

      {Toast}
    </FormShell>
  );
}

/*
── Mongoose Schema ────────────────────────────────────────────────────────────
const AuthFormSchema = new Schema({
  formType:     { type: String, default: "authorization_form" },
  patient:      { name: String },
  practiceName: String,
  authorizations: {
    payBenefits:    { type: Boolean, default: false },
    releaseInfo:    { type: Boolean, default: false },
    patientPicture: { type: Boolean, default: false },
    privacyNotice:  { type: Boolean, default: false },
  },
  printName: String,
  authorization: { responsibleParty:String, relationship:String, sigDate:Date, signatureDataURL:String },
  meta: { submittedAt:Date, formVersion:String },
}, { timestamps:true, collection:"authorization_forms" });
*/
